import React from 'react'

import { toJS } from 'mobx'

import {
  ISitePermitFilterInput,
  PermitFieldType,
  SitePermitStatus,
} from '~/client/graph'
import { LogisticsFilterType } from '~/client/src/shared/enums/LogisticsFilterType'
import { getIdFromLocation } from '~/client/src/shared/utils/logisticsHelper'
import { UNASSIGNED } from '~/client/src/shared/utils/ZoneLevelLocationConstants'

import DesktopInitialState from '../../stores/DesktopInitialState'

type MapFn<T> = (key: string) => T
function extractFilterKeys<T extends string | null>(
  rawKeys: string[],
  mapFn: MapFn<T> = key => key as unknown as T,
  permitFieldType: PermitFieldType | undefined,
  includeUnassigned: PermitFieldType[],
  removeUnassigned: boolean = true,
): T[] {
  const keys = [...rawKeys]
  const unassignedIndex = keys.indexOf(UNASSIGNED)
  if (removeUnassigned && unassignedIndex !== -1) {
    keys.splice(unassignedIndex, 1)
    if (permitFieldType) includeUnassigned.push(permitFieldType)
  }
  return keys.map(mapFn)
}

type FilterConfigEntry<T> = {
  key: string
  filterType: LogisticsFilterType
  mapFn?: MapFn<T>
  permitFieldType?: PermitFieldType
  removeUnassigned?: boolean
}

const filterConfig: FilterConfigEntry<any>[] = [
  {
    key: 'statuses',
    filterType: LogisticsFilterType.Status,
    mapFn: key => key as SitePermitStatus,
  },
  {
    key: 'companyIds',
    filterType: LogisticsFilterType.Company,
    mapFn: key => key,
    permitFieldType: PermitFieldType.Company,
  },
  {
    key: 'locationIds',
    filterType: LogisticsFilterType.Location,
    mapFn: getIdFromLocation,
    permitFieldType: PermitFieldType.Location,
  },
  {
    key: 'equipmentIds',
    filterType: LogisticsFilterType.Equipment,
    mapFn: getIdFromLocation,
    permitFieldType: PermitFieldType.Equipment,
  },
  {
    key: 'categoryIds',
    filterType: LogisticsFilterType.Category,
    mapFn: key => (key === UNASSIGNED ? null : key),
    removeUnassigned: false,
  },
  { key: 'initialTypes', filterType: LogisticsFilterType.Type },
  {
    key: 'assigneeIds',
    filterType: LogisticsFilterType.ResponsibleContact,
    mapFn: key => key,
    permitFieldType: PermitFieldType.Assignee,
  },
  {
    key: 'files',
    filterType: LogisticsFilterType.Attachment,
    mapFn: key => key,
    permitFieldType: PermitFieldType.File,
  },
]

export default function useWorkflowsFilter(
  appState: DesktopInitialState,
): ISitePermitFilterInput {
  return React.useMemo(() => {
    const { fieldsMap } = appState.workflowsFilters

    const includeUnassignedFields: PermitFieldType[] = []

    const filters = filterConfig.reduce(
      (acc, { key, filterType, mapFn, permitFieldType, removeUnassigned }) => {
        acc[key] = extractFilterKeys(
          Array.from(fieldsMap[filterType]?.initialFilterOptions.keys() || []),
          mapFn,
          permitFieldType,
          includeUnassignedFields,
          removeUnassigned,
        )
        return acc
      },
      {} as Record<string, any>,
    )

    return {
      includeUnassignedFields,
      ...filters,
    }
  }, [toJS(appState.workflowsFilters.fieldsMap)])
}
